/**
 * @fileoverview Own validated, persisted browser preferences and navigation state for Documents. `dataApi.js` separately owns server metadata snapshots. Tabs, selected views, and migration from older local preferences describe workspace context rather than canonical hierarchy or permissions. Identity resets must coordinate both stores without placing file bytes or editor objects in Redux.
 */

import { createSlice } from '@reduxjs/toolkit';

export const DOCUMENTS_WORKSPACE_STORAGE_KEY = 'kc.documents.workspace.v2';
export const LEGACY_DOCUMENTS_REGISTRY_STATE_KEY = 'kc.documents.registry-state.v1';
export const LEGACY_DOCUMENTS_FOLDER_TABS_KEY = 'kc.documents.folders.tabs.v1';
export const LEGACY_DOCUMENTS_FOLDER_VIEW_KEY = 'kc.documents.folders.view';

export const DEFAULT_DOCUMENTS_FOLDER_TAB = {
  id: 'folder-tab-default',
  folderId: '',
  folderKey: '',
  label: 'Folders',
  title: 'Folders',
  previewDocumentId: '',
};

export const DEFAULT_DOCUMENTS_WORKSPACE_STATE = {
  search: '',
  matterNumber: '',
  folderPath: '',
  dateFrom: '',
  dateTo: '',
  registryStatus: 'all',
  registryDisplay: 'folders',
  registrySort: 'modified_desc',
  registryViewMode: 'list',
  folderView: 'columns',
  organizationFilters: {
    fileType: '',
    owner: '',
    sizeMin: '',
    sizeMax: '',
    tags: '',
    sharing: '',
    source: '',
    createdFrom: '',
    createdTo: '',
    modifiedFrom: '',
    modifiedTo: '',
    trashStatus: 'active',
    favorite: false,
    pinned: false,
  },
  documentFirst: 0,
  nonMatterFolderPage: 0,
  matterFolderPage: 0,
  activeFolder: null,
  folderSearch: '',
  matterSuggestionSelections: {},
  documentUrl: '',
  folderTabs: [{ ...DEFAULT_DOCUMENTS_FOLDER_TAB }],
  activeFolderTabId: DEFAULT_DOCUMENTS_FOLDER_TAB.id,
};

const STRING_FIELDS = [
  'search',
  'matterNumber',
  'folderPath',
  'dateFrom',
  'dateTo',
  'folderSearch',
];



function storedObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}



function nonnegativeInteger(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.floor(number) : 0;
}



function normalizeFolderTab(tab) {
  if (!tab || typeof tab.id !== 'string') return null;
  return {
    id: tab.id,
    folderId: String(tab.folderId || ''),
    folderKey: String(tab.folderKey || ''),
    label: String(tab.label || 'Folders'),
    title: String(tab.title || tab.label || 'Folders'),
    previewDocumentId: String(tab.previewDocumentId || ''),
  };
}



function normalizeDocumentUrl(value) {
  const url = typeof value === 'string' ? value : '';
  return /^\/documents\/[^/?#]+(?:\?[^#]*)?$/.test(url) ? url : '';
}



export function normalizeDocumentsWorkspaceState(value = {}) {
  const source = storedObject(value);
  const state = {
    ...DEFAULT_DOCUMENTS_WORKSPACE_STATE,
    ...Object.fromEntries(STRING_FIELDS.map((field) => [field, typeof source[field] === 'string' ? source[field] : ''])),
    registryStatus: ['locked', 'all'].includes(source.registryStatus) ? source.registryStatus : 'all',
    registryDisplay: ['files', 'folders', 'templates', 'review'].includes(source.registryDisplay)
      ? source.registryDisplay
      : 'folders',
    registrySort: ['modified_desc', 'modified_asc', 'name_asc', 'name_desc'].includes(source.registrySort)
      ? source.registrySort
      : 'modified_desc',
    registryViewMode: ['list', 'grid'].includes(source.registryViewMode) ? source.registryViewMode : 'list',
    folderView: ['columns', 'tree'].includes(source.folderView) ? source.folderView : 'columns',
    organizationFilters: {
      ...DEFAULT_DOCUMENTS_WORKSPACE_STATE.organizationFilters,
      ...storedObject(source.organizationFilters),
    },
    documentFirst: Math.floor(nonnegativeInteger(source.documentFirst) / 50) * 50,
    nonMatterFolderPage: nonnegativeInteger(source.nonMatterFolderPage ?? source.simulFolderPage),
    matterFolderPage: nonnegativeInteger(source.matterFolderPage ?? source.filedFolderPage),
    activeFolder: source.activeFolder && typeof source.activeFolder === 'object' ? source.activeFolder : null,
    matterSuggestionSelections: storedObject(source.matterSuggestionSelections),
    documentUrl: normalizeDocumentUrl(source.documentUrl),
  };
  const folderTabs = Array.isArray(source.folderTabs)
    ? source.folderTabs.slice(0, 20).map(normalizeFolderTab).filter(Boolean)
    : [];
  state.folderTabs = folderTabs.length > 0 ? folderTabs : [{ ...DEFAULT_DOCUMENTS_FOLDER_TAB }];
  state.activeFolderTabId = state.folderTabs.some((tab) => tab.id === source.activeFolderTabId)
    ? source.activeFolderTabId
    : state.folderTabs[0].id;
  return state;
}



function readJson(storage, key) {
  if (!storage) return null;
  try {
    return JSON.parse(storage.getItem(key) || 'null');
  } catch (_error) {
    return null;
  }
}



export function loadDocumentsWorkspaceState() {
  if (typeof window === 'undefined') return normalizeDocumentsWorkspaceState();
  const storedWorkspace = readJson(window.localStorage, DOCUMENTS_WORKSPACE_STORAGE_KEY);
  if (storedWorkspace) return normalizeDocumentsWorkspaceState(storedWorkspace);

  const legacyRegistry = storedObject(readJson(window.sessionStorage, LEGACY_DOCUMENTS_REGISTRY_STATE_KEY));
  const legacyTabs = storedObject(readJson(window.sessionStorage, LEGACY_DOCUMENTS_FOLDER_TABS_KEY));
  let legacyFolderView = '';
  try {
    legacyFolderView = window.localStorage.getItem(LEGACY_DOCUMENTS_FOLDER_VIEW_KEY) || '';
  } catch (_error) {
    legacyFolderView = '';
  }
  return normalizeDocumentsWorkspaceState({
    ...legacyRegistry,
    folderView: legacyFolderView,
    folderTabs: legacyTabs.tabs,
    activeFolderTabId: legacyTabs.activeTabId,
  });
}



export function persistDocumentsWorkspaceState(state) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      DOCUMENTS_WORKSPACE_STORAGE_KEY,
      JSON.stringify(normalizeDocumentsWorkspaceState(state))
    );
  } catch (_error) {
    // Redux remains the source of truth for this browser session when storage is unavailable.
  }
}

const documentsWorkspaceSlice = createSlice({
  name: 'documents',
  initialState: DEFAULT_DOCUMENTS_WORKSPACE_STATE,
  reducers: {


    setDocumentsWorkspaceField(state, action) {
      const { field, value } = action.payload || {};
      if (!Object.prototype.hasOwnProperty.call(DEFAULT_DOCUMENTS_WORKSPACE_STATE, field)) return;
      state[field] = value;
    },


    setDocumentsFolderTabs(state, action) {
      const normalized = normalizeDocumentsWorkspaceState({
        ...state,
        folderTabs: action.payload?.tabs,
        activeFolderTabId: action.payload?.activeTabId,
      });
      state.folderTabs = normalized.folderTabs;
      state.activeFolderTabId = normalized.activeFolderTabId;
    },
  },
});

export const {
  setDocumentsWorkspaceField,
  setDocumentsFolderTabs,
} = documentsWorkspaceSlice.actions;

export default documentsWorkspaceSlice.reducer;
